/**
 * Retargeting Pixel Tracking Library
 * Handles Google Tag Manager and Facebook/Meta Pixel tracking
 * Only loads pixels if environment variables are configured
 */

// Type declarations for GTM and Facebook Pixel
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    fbq: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
    };
    _fbq?: typeof window.fbq;
  }
}

// Environment variable IDs
const GTM_ID = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID as string | undefined;
const FB_PIXEL_ID = import.meta.env.VITE_FACEBOOK_PIXEL_ID as string | undefined;

// Check if pixels are configured
export const hasGTM = (): boolean => !!GTM_ID;
export const hasFBPixel = (): boolean => !!FB_PIXEL_ID;
export const hasAnyPixel = (): boolean => hasGTM() || hasFBPixel();

// Get pixel IDs (for script injection)
export const getGTMId = (): string | undefined => GTM_ID;
export const getFBPixelId = (): string | undefined => FB_PIXEL_ID;

/**
 * Initialize dataLayer for GTM
 */
export const initDataLayer = (): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
};

/**
 * Push event to GTM dataLayer
 */
export const pushToDataLayer = (event: string, data?: Record<string, unknown>): void => {
  if (!GTM_ID || typeof window === 'undefined') return;
  
  initDataLayer();
  window.dataLayer.push({ event, ...data });
};

/**
 * Track Facebook Pixel event
 */
export const trackFBEvent = (eventName: string, params?: Record<string, unknown>): void => {
  if (!FB_PIXEL_ID || typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  
  if (params) {
    window.fbq('track', eventName, params);
  } else {
    window.fbq('track', eventName);
  }
};

/**
 * Track PageView on both GTM and Facebook Pixel
 */
export const trackPixelPageView = (): void => {
  // GTM - page_view event
  pushToDataLayer('page_view', {
    page_path: window.location.pathname,
    page_url: window.location.href,
    page_title: document.title,
  });
  
  // Facebook Pixel - PageView is tracked on init, but we can track SPA navigation
  trackFBEvent('PageView');
};

/**
 * Track ViewContent on listing detail pages
 */
export interface ViewContentParams {
  listingId: string;
  price: number;
  address: string;
  city?: string;
  state?: string;
  contentType?: string;
}

export const trackPixelViewContent = (params: ViewContentParams): void => {
  const { listingId, price, address, city, state, contentType = 'home' } = params;
  
  // GTM - view_content event
  pushToDataLayer('view_content', {
    listing_id: listingId,
    price: price,
    address: address,
    city: city,
    state: state,
    content_type: contentType,
    currency: 'USD',
  });
  
  // Facebook Pixel - ViewContent
  trackFBEvent('ViewContent', {
    content_ids: [listingId],
    content_type: 'product',
    content_name: address,
    content_category: contentType,
    value: price,
    currency: 'USD',
  });
};

/**
 * Track Lead on form submissions
 */
export interface LeadParams {
  formType: string;
  listingId?: string;
  value?: number;
  contentName?: string;
}

export const trackPixelLead = (params: LeadParams): void => {
  const { formType, listingId, value, contentName } = params;
  
  // GTM - lead event
  pushToDataLayer('lead', {
    form_type: formType,
    listing_id: listingId,
    value: value,
    currency: 'USD',
  });
  
  // Facebook Pixel - Lead
  trackFBEvent('Lead', {
    content_name: contentName || formType,
    content_category: formType,
    content_ids: listingId ? [listingId] : undefined,
    value: value,
    currency: 'USD',
  });
};

/**
 * Track InitiateCheckout when starting showing request
 */
export interface InitiateCheckoutParams {
  listingId: string;
  price: number;
  address: string;
}

export const trackPixelInitiateCheckout = (params: InitiateCheckoutParams): void => {
  const { listingId, price, address } = params;
  
  // GTM - initiate_checkout event
  pushToDataLayer('initiate_checkout', {
    listing_id: listingId,
    price: price,
    address: address,
    currency: 'USD',
  });
  
  // Facebook Pixel - InitiateCheckout
  trackFBEvent('InitiateCheckout', {
    content_ids: [listingId],
    content_type: 'product',
    content_name: address,
    value: price,
    currency: 'USD',
    num_items: 1,
  });
};

/**
 * Log message when pixels are not configured
 */
export const logPixelStatus = (): void => {
  if (!hasAnyPixel()) {
    console.log(
      'Retargeting pixels not loaded - add VITE_GOOGLE_TAG_MANAGER_ID or VITE_FACEBOOK_PIXEL_ID to env vars'
    );
  } else {
    if (hasGTM()) console.log('Google Tag Manager loaded');
    if (hasFBPixel()) console.log('Facebook Pixel loaded');
  }
};
